/**
 * @file Helpers for managing URL query parameters.
 */

/**
 * Retrieves the value of a specified query parameter from the current URL.
 *
 * @param name - The name of the query parameter to retrieve.
 * @returns The value of the specified query parameter, or null if the parameter is not present.
 */
export function getQueryParam(name: string) {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
}

/**
 * Adds a single query parameter to the current URL.
 *
 * @param name - The name of the query parameter to add.
 * @param value - The value of the query parameter to add.
 * @param useReplaceState - Optional. If true, the URL will be updated using `replaceState` instead of `pushState`. Defaults to false.
 */
export function addQueryParam(name: string, value: string, useReplaceState = false) {
  addQueryParams([{ name, value }], useReplaceState);
}

/**
 * Adds or updates query parameters in the current URL and updates the browser history.
 *
 * @param paramsArray - An array of objects containing `name` and `value` properties for the query parameters to be added or updated.
 * @param useReplaceState - A boolean indicating whether to use `replaceState` (true) or `pushState` (false) to update the browser history. Defaults to false.
 *
 * @example
 * ```typescript
 * addQueryParams([{ name: 'a', value: '123' }], true);
 * ```
 */
export function addQueryParams(
  paramsArray: { name: string; value: string }[],
  useReplaceState = false
) {
  // Get the current URL
  const currentUrl = new URL(window.location.href);

  // Get the existing query parameters
  const queryParams = new URLSearchParams(currentUrl.search);

  // Add or update the query parameters from the array
  paramsArray.forEach(({ name, value }) => {
    queryParams.set(name, value);
  });

  // Create a new URL with the updated query parameters
  const newUrl = `${currentUrl.origin}${currentUrl.pathname}?${queryParams.toString()}${currentUrl.hash}`;

  // Determine whether to use pushState or replaceState
  if (useReplaceState) {
    // Replace the current URL in the browser history without adding a new entry
    window.history.replaceState({ path: newUrl }, "", newUrl);
  } else {
    // Push the new URL to the browser history without reloading the page
    window.history.pushState({ path: newUrl }, "", newUrl);
  }
}

/**
 * Removes specified query parameters from the current URL without reloading the page.
 *
 * @param {string | string[]} keysToRemove - A single query parameter key or an array of keys to be removed from the URL.
 *
 * @remarks
 * - If `keysToRemove` is not an array, it will be converted to an array.
 * - If none of the specified keys are found in the URL, the function will return without making any changes.
 * - The URL is updated using `window.history.replaceState` to avoid reloading the page.
 *
 * @example
 * ```typescript
 * // URL before: http://example.com/?a=1&b=2&c=3
 * removeParamsFromURL(['a', 'c']);
 * // URL after: http://example.com/?b=2
 * ```
 */
export const removeParamsFromURL = (keysToRemove: string | string[]) => {
  if (!keysToRemove) return;

  keysToRemove = Array.isArray(keysToRemove) ? keysToRemove : [keysToRemove];
  const urlParams = new URLSearchParams(window.location.search);
  const filteredKeysToRemove = keysToRemove.filter((key) => !!urlParams.get(key));

  // Cancel If Empty
  if (!keysToRemove.length || !filteredKeysToRemove.length) {
    return;
  }

  const url = window.location.href;
  const urlParts = url.split("?");

  if (urlParts.length >= 2) {
    const queryParams = urlParts[1].split("&");
    const updatedParams = queryParams.filter((param) => {
      const paramName = param.split("=")[0];
      return !keysToRemove.includes(paramName);
    });

    const updatedURL =
      urlParts[0] + (updatedParams.length > 0 ? "?" + updatedParams.join("&") : "");

    // Update the URL in the browser without reloading the page
    window.history.replaceState({}, "", updatedURL);
  }
};
